/**
 * On-demand OAuth access token for Google People directory (read-only).
 * Same GIS pattern as googleDrivePicker.ts; add `directory.readonly` to OAuth consent in GCP.
 */

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';

const DIRECTORY_SCOPE = 'https://www.googleapis.com/auth/directory.readonly';

let gisLoaded = false;

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
    document.head.appendChild(script);
  });
}

async function loadGis(): Promise<void> {
  if (gisLoaded) return;
  await loadScript('https://accounts.google.com/gsi/client');
  gisLoaded = true;
}

/**
 * Returns a short-lived access_token for the directory scope.
 * Do not pass `prompt: 'consent'` — that forces the consent screen on every click.
 * Omitting prompt lets Google reuse an existing grant until the token expires (~1h).
 */
export async function requestDirectoryReadonlyToken(): Promise<string> {
  await loadGis();
  if (!GOOGLE_CLIENT_ID) {
    throw new Error('VITE_GOOGLE_CLIENT_ID is not configured');
  }

  return new Promise((resolve, reject) => {
    const tokenClient = (window as unknown as { google: { accounts: { oauth2: {
      initTokenClient: (opts: Record<string, unknown>) => { requestAccessToken: (o?: { prompt?: string }) => void };
    } } } }).google.accounts.oauth2.initTokenClient({
      client_id: GOOGLE_CLIENT_ID,
      scope: DIRECTORY_SCOPE,
      callback: (response: { error?: string; error_description?: string; access_token?: string }) => {
        if (response.error) {
          reject(new Error(response.error_description || response.error));
          return;
        }
        if (!response.access_token) {
          reject(new Error('No access token returned'));
          return;
        }
        resolve(response.access_token);
      },
      error_callback: (error: { message?: string }) => {
        reject(new Error(error?.message || 'Token request failed'));
      },
    });

    tokenClient.requestAccessToken();
  });
}
